import AdminLayout from '@/app/AdminLayout';
import { AlertBannerPage } from '@/app/appStatus/AlertBannerPage';
import { AppStatusOverviewPage } from '@/app/appStatus/AppStatusOverviewPage';
import { MaintenancePage } from '@/app/appStatus/MaintenancePage';
import { OtaUpdatePage } from '@/app/appStatus/OtaUpdatePage';
import { RequiredUpgradePage } from '@/app/appStatus/RequiredUpgradePage';
import { BrandRequestDetailsPage } from '@/app/brand-requests/BrandRequestDetailsPage';
import { BrandRequestsListPage } from '@/app/brand-requests/BrandRequestsListPage';
import { BrandDetailsPage } from '@/app/brands/BrandDetailsPage';
import { BrandsListPage } from '@/app/brands/BrandsListPage';
import CompanyDetailsPage from '@/app/companies/CompanyDetailsPage';
import { CompaniesListPage } from '@/app/companies/CompaniesListPage';
import { ConsiderationTypeDetailsPage } from '@/app/considerations/ConsiderationTypeDetailsPage';
import { ConsiderationTypesPage } from '@/app/considerations/ConsiderationTypesPage';
import { DefaultTaskScreenPage } from '@/app/considerations/DefaultTaskScreenPage';
import { TaskTypesPage } from '@/app/tasks/TaskTypesPage';
import DashboardPage from '@/app/dashboard/DashboardPage';
import { LessonTypeDetailsPage } from '@/app/lessons/LessonTypeDetailsPage';
import { LessonTypesPage } from '@/app/lessons/LessonTypesPage';
import DomainEventDetailsPage from '@/app/domainEvents/DomainEventDetailsPage';
import DomainEventsPage from '@/app/domainEvents/DomainEventsPage';
import CandidateDetailsPage from '@/app/equipment-type-candidates/CandidateDetailsPage';
import { CoreFieldsPage } from '@/app/equipment/CoreFieldsPage';
import { DocumentDetailsPage } from '@/app/equipment/DocumentDetailsPage';
import { DocumentsListPage } from '@/app/equipment/DocumentsListPage';
import ModelDetailsPage from '@/app/equipment/ModelDetailsPage';
import ModelsListPage from '@/app/equipment/ModelsListPage';
import { TestLibraryListPage } from '@/app/equipmentTestLibrary/TestLibraryListPage';
import { EquipmentTypesPage } from '@/app/equipmentTypes/EquipmentTypesPage';
import { GuideEditorPage } from '@/app/guide/GuideEditorPage';
import { HomeDetailsPage } from '@/app/homes/HomeDetailsPage';
import HomeListPage from '@/app/homes/HomeListPage';
import { EquipmentTypePage } from '@/app/itemType/EquipmentTypePage';
import { JobsPage } from '@/app/jobs/JobsPage';
import { NewSpacePage } from '@/app/space/NewSpacePage';
import { SpacePage } from '@/app/space/SpacePage';
import { SpacesListPage } from '@/app/space/SpacesListPage';
import { RecallsRoutes } from '@/app/recalls/RecallsRoutes';
import { SharedNameplatesPage } from '@/app/shared-nameplates/SharedNameplatesPage';
import { UsersRoutes } from '@/app/users/UsersRoutes';
import { DEFAULT_EQUIPMENT_SPACE_ID, type SpaceId } from '@rock/types';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

export default function AppRouter() {
    return (
        <Routes>
            <Route element={<AdminLayout />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/products">
                    <Route index element={<Navigate replace to="models" />} />
                    <Route path="models" element={<ModelsListPage />} />
                    <Route path="models/:modelId/*" element={<ModelDetailsPage />} />
                    <Route path="documents" element={<DocumentsListPage />} />
                    <Route path="documents/:docId/*" element={<DocumentDetailsPage />} />
                    <Route path="brands">
                        <Route index element={<BrandsListPage />} />
                        <Route path=":brandId/*" element={<BrandDetailsPage />} />
                    </Route>
                    <Route path="brand-requests">
                        <Route index element={<BrandRequestsListPage />} />
                        <Route path=":requestId" element={<BrandRequestDetailsPage />} />
                    </Route>
                    <Route path="shared-nameplates" element={<SharedNameplatesPage />} />
                    <Route path="shared-nameplates/:key" element={<SharedNameplatesPage />} />
                    <Route path="recalls/*" element={<RecallsRoutes />} />
                </Route>
                <Route path="/equipment">
                    <Route index element={<Navigate replace to="types" />} />
                    <Route path="types">
                        <Route index element={<RedirectPreservingSearch to="catalog" />} />
                        <Route path="catalog" element={<EquipmentTypesPage tab="catalog" />} />
                        <Route path="candidates" element={<EquipmentTypesPage tab="candidates" />} />
                        <Route path="retired" element={<EquipmentTypesPage tab="retired" />} />
                        <Route path="families" element={<EquipmentTypesPage tab="families" />} />
                        <Route path="families/:familyId" element={<EquipmentTypesPage tab="families" />} />
                        {/* Static segments outrank this, so a type can't shadow a tab. */}
                        <Route path=":equipmentTypeId/*" element={<EquipmentTypePage />} />
                    </Route>
                    {/* The candidates list moved under Equipment Types; its details page did not. */}
                    <Route path="candidates" element={<RedirectPreservingSearch to="/equipment/types/candidates" />} />
                    <Route path="candidates/:candidateId" element={<CandidateDetailsPage />} />
                    <Route path="default-guide" element={<DefaultEquipmentGuidePage />} />
                    <Route path="default-guide/:flowId" element={<DefaultEquipmentGuidePage />} />
                    <Route path="library" element={<TestLibraryListPage />} />
                    <Route path="core-fields" element={<CoreFieldsPage />} />
                </Route>
                <Route path="/directory">
                    <Route index element={<Navigate replace to="homes" />} />
                    <Route path="homes">
                        <Route index element={<HomeListPage />} />
                        <Route path=":homeId/*" element={<HomeDetailsPage />} />
                    </Route>
                    <Route path="users/*" element={<UsersRoutes />} />
                    <Route path="companies">
                        <Route index element={<CompaniesListPage />} />
                        <Route path=":companyId" element={<CompanyDetailsPage />} />
                    </Route>
                </Route>
                <Route path="/content">
                    <Route index element={<Navigate replace to="considerations" />} />
                    <Route path="considerations">
                        <Route index element={<ConsiderationTypesPage />} />
                        <Route path=":typeId/*" element={<ConsiderationTypeDetailsPage />} />
                    </Route>
                    <Route path="tasks" element={<TaskTypesPage />} />
                    <Route path="lessons">
                        <Route index element={<LessonTypesPage />} />
                        <Route path=":typeId" element={<LessonTypeDetailsPage />} />
                    </Route>
                    <Route path="default-task-screen" element={<DefaultTaskScreenPage />} />
                </Route>
                <Route path="/system">
                    <Route index element={<Navigate replace to="status" />} />
                    <Route path="status">
                        <Route index element={<AppStatusOverviewPage />} />
                        <Route path="alert" element={<AlertBannerPage />} />
                        <Route path="upgrade" element={<RequiredUpgradePage />} />
                        <Route path="ota" element={<OtaUpdatePage />} />
                        <Route path="maintenance" element={<MaintenancePage />} />
                    </Route>
                    <Route path="jobs/*" element={<JobsPage />} />
                    <Route path="domain-events">
                        <Route index element={<DomainEventsPage />} />
                        <Route path=":eventId" element={<DomainEventDetailsPage />} />
                    </Route>
                </Route>
                <Route path="/spaces" element={<SpacesListPage />} />
                <Route path="/spaces/new" element={<NewSpacePage />} />
                <Route path="/spaces/:spaceId/*" element={<SpaceRoutes />} />
            </Route>
        </Routes>
    );
}

const DEFAULT_GUIDE_PARAMS = { spaceId: DEFAULT_EQUIPMENT_SPACE_ID, equipmentTypeId: undefined };

const DefaultEquipmentGuidePage = () => {
    const { flowId } = useParams<{ flowId: string }>();
    return <GuideEditorPage params={DEFAULT_GUIDE_PARAMS} flowId={flowId} editBase="/equipment/default-guide" />;
};

/** A redirect that keeps the query string, so a filtered list stays filtered across it. */
const RedirectPreservingSearch = ({ to }: { to: string }) => {
    const { search } = useLocation();
    return <Navigate replace to={{ pathname: to, search }} />;
};

const SpaceRoutes = () => {
    const { spaceId = '' as SpaceId } = useParams<{ spaceId: SpaceId }>();

    return (
        <Routes>
            {/* Equipment types left the space tree — send the old bookmarks to where they live now. */}
            <Route path="equipment/:equipmentTypeId/*" element={<LegacyEquipmentTypeRedirect />} />
            {/* A guide is picked from the Guides table now, not opened straight from the tab. */}
            <Route path="guide" element={<Navigate replace to={`/spaces/${spaceId}/guides`} />} />
            {/* Info types are edited from the space's Key Info tab now, not on a page of their own. */}
            <Route path="info/:infoTypeId/*" element={<Navigate replace to={`/spaces/${spaceId}/info`} />} />
            <Route path="*" element={<SpacePage spaceId={spaceId} />} />
        </Routes>
    );
};

const LegacyEquipmentTypeRedirect = () => {
    const { equipmentTypeId = '', '*': rest = '' } = useParams();
    // `guide` was the editor itself; it's the Guides table that lives at the tab now.
    const tail = rest === 'guide' ? 'guides' : rest;
    return <Navigate replace to={`/equipment/types/${equipmentTypeId}${tail ? `/${tail}` : ''}`} />;
};
